import React from "react";
import { Link } from "react-router-dom";
import { FactList, FicheHeader, FicheSection, Timeline } from "../../../common/Fiche";
import tools from "../../../../helpers/tools";
import { estAnonymise } from "../../../../helpers/allocataire";
import StatutDeclarationBadge from "./StatutDeclarationBadge";
import {
  COULEUR_KO,
  COULEUR_NEUTRE,
  COULEUR_OK,
  Fait,
  formatDateHeure,
  LigneReponse,
} from "./blocsSanctionFT";
import "./detail-proposition.scss";

/**
 * Ce que l'EP a décidé, en une phrase. Une EP qui n'a rien prononcé envoie un
 * barème à zéro : « Suspension de 0 % pendant 0 mois » n'est pas une décision
 * lisible, c'est « Aucune sanction » (même lecture que sur une proposition).
 * Une inscription dont l'EP n'a pas encore tranché n'a pas de barème du tout.
 */
export const libelleDecisionEP = (ep) => {
  if (!ep) return null;
  if (ep.pourcentageReduction == null || ep.dureeEnMois == null) return "Décision EP non arrêtée";
  if (ep.pourcentageReduction === 0 && ep.dureeEnMois === 0) return "Aucune sanction";
  return `${ep.typeLabel} de ${ep.pourcentageReduction} % pendant ${ep.dureeEnMois} mois`;
};

/**
 * La teinte d'une ligne du tableau. Une EP sans sanction reste neutre : c'est
 * une issue voulue, pas une anomalie — la peindre en rouge enverrait un agent
 * la corriger pour rien. Une sanction que France Travail ne peut pas recevoir
 * est rouge : close, mais à traiter à la main dans NIMS (ST1-2209).
 */
export const tonLigneDeclaration = (d) => {
  if (d.statut === "NON_DECLARABLE") return null;
  if (d.statut === "NON_TRANSMISSIBLE") return "ko";
  if (d.relancable) return "warn";
  if (d.statut === "DECLAREE" || d.statut === "RADIATION_DEMANDEE") return "ok";
  return null;
};

/**
 * Ce que France Travail a enregistré, résumé pour la seconde carte : son ton,
 * son libellé principal et sa mention. C'est la question que se pose l'agent —
 * ce qu'on a décidé est-il bien arrivé, et sous quelle référence.
 */
export const enregistrementFT = (d) => {
  if (d.statut === "NON_DECLARABLE") {
    return {
      ton: "pending",
      label: "Rien ne sera transmis",
      mention: d.raisonNonDeclarable,
    };
  }
  if (d.statut === "NON_TRANSMISSIBLE") {
    return {
      ton: "ko",
      label: "Non transmise à France Travail",
      mention: d.raisonNonDeclarable,
    };
  }
  if (!d.declaration) {
    return {
      ton: d.blocage ? "warn" : "pending",
      label: "Pas encore transmise",
      mention: d.blocage ? "La dernière tentative a échoué" : "En attente du prochain envoi",
    };
  }
  if (d.radiationDE && !d.radiation) {
    return {
      ton: "warn",
      label: "Sanction enregistrée, radiation manquante",
      mention: "La radiation prononcée par l'EP n'a pas encore été transmise",
    };
  }
  return {
    ton: "ok",
    label: "Sanction enregistrée",
    mention: `Transmise le ${tools.formatDate(d.declaration.date)}`,
  };
};

/**
 * Les envois et ce que France Travail en a dit, du plus récent au plus ancien —
 * même lecture que sur une proposition reçue.
 */
export const chronologieDeclaration = (d) => {
  const items = [];

  if (d.decisionEP?.dateFinalisationEP) {
    items.push({
      id: "ep",
      date: d.decisionEP.dateFinalisationEP,
      icon: "gavel",
      color: COULEUR_NEUTRE,
      title: "Décision de l'EP finalisée",
      body: d.decisionEP.motifLabel ? (
        <p className="mb-0 small text-muted">{d.decisionEP.motifLabel}</p>
      ) : null,
    });
  }

  if (d.dateCreation) {
    items.push({
      id: "creation",
      date: d.dateCreation,
      icon: "flag",
      color: COULEUR_NEUTRE,
      title: "Sanction repérée par ESPOAR",
    });
  }

  if (d.declaration) {
    items.push({
      id: "declaration",
      date: d.declaration.date,
      icon: "paper-plane",
      color: COULEUR_OK,
      variant: "ft",
      title: "Sanction déclarée à France Travail",
      body: (
        <p className="mb-0 small text-muted">
          {d.identifiantConsequenceSanction
            ? `Conséquence n° ${d.identifiantConsequenceSanction}`
            : "Sans identifiant de conséquence"}
          {d.identifiantManquement ? ` · manquement n° ${d.identifiantManquement}` : ""}
          {` · code France Travail : ${d.declaration.codeSortie}`}
        </p>
      ),
    });
  }

  if (d.radiation) {
    items.push({
      id: "radiation",
      date: d.radiation.dateDemande,
      icon: d.radiation.effectuee ? "user-times" : "minus-circle",
      color: d.radiation.effectuee ? COULEUR_OK : COULEUR_NEUTRE,
      variant: "ft",
      title: d.radiation.label,
      body: (
        <p className="mb-0 small text-muted">
          Code France Travail : {d.radiation.codeSortie}
        </p>
      ),
    });
  }

  if (d.blocage) {
    items.push({
      id: "blocage",
      date: d.blocage.depuis,
      icon: "exclamation-triangle",
      color: COULEUR_KO,
      title: "Dernière tentative en échec",
      body: <p className="mb-0 small font-weight-bold">{d.blocage.raison}</p>,
    });
  }

  return items.sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Fiche d'une sanction déclarée par la CAF à France Travail, ouverte dans le
 * panneau latéral : le tableau ne garde que de quoi repérer et trier, tout ce
 * qui explique un dossier vit ici.
 */
const DetailDeclarationSanctionCAF = ({ declaration: d, onRelancer, enCoursDeRelance = false }) => {
  const nomComplet = d.allocataire
    ? `${d.allocataire.nom || ""} ${d.allocataire.prenom || ""}`.trim()
    : null;
  const ep = d.decisionEP;
  const ft = enregistrementFT(d);
  const aUnEnvoi = Boolean(d.declaration || d.radiation);

  return (
    <div className="detail-proposition fiche-page fiche-page--modal">
      <FicheHeader
        headingLevel="h2"
        eyebrow={
          d.identifiantConsequenceSanction
            ? `Sanction déclarée par la CAF · conséquence n° ${d.identifiantConsequenceSanction}`
            : "Sanction déclarée par la CAF"
        }
        title={
          d.allocataire && nomComplet ? (
            <Link
              to={tools.getRecipientLinkFromParams(d.allocataire.type, d.allocataire.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {nomComplet}
            </Link>
          ) : (
            "Allocataire non rattaché"
          )
        }
        pills={<StatutDeclarationBadge statut={d.statut} statutLabel={d.statutLabel} />}
        actions={
          d.relancable && onRelancer && !estAnonymise(d.allocataire) ? (
            <button
              type="button"
              className={
                "btn btn-sm detail-declaration__relancer " +
                (d.blocage ? "btn-warning" : "btn-default")
              }
              disabled={enCoursDeRelance}
              onClick={() => onRelancer(d.inscriptionEPId)}
              title={
                d.blocage
                  ? "Réessayer après avoir corrigé ce qui bloquait"
                  : "Redemander l'envoi sans attendre le passage automatique"
              }
            >
              <i className={"fa mr-1 " + (enCoursDeRelance ? "fa-spinner fa-spin" : "fa-redo")} aria-hidden="true" />
              Relancer
            </button>
          ) : null
        }
      >
        <FactList
          facts={[
            {
              id: "seance",
              label: "Séance",
              value: ep?.dateEP ? `EP du ${tools.formatDate(ep.dateEP)}` : null,
            },
            {
              id: "finalisation",
              label: "EP finalisée le",
              value: ep?.dateFinalisationEP ? tools.formatDate(ep.dateFinalisationEP) : null,
            },
            {
              id: "reperee",
              label: "Repérée le",
              value: d.dateCreation ? formatDateHeure(d.dateCreation) : null,
            },
          ].filter((f) => f.value)}
        />
      </FicheHeader>

      {d.blocage && (
        <div className="alert alert-danger mb-0" role="alert">
          <strong>
            <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
            Dernière tentative en échec
          </strong>
          <div>{d.blocage.raison}</div>
          <small className="text-muted">Depuis le {formatDateHeure(d.blocage.depuis)}</small>
        </div>
      )}

      {d.statut === "NON_TRANSMISSIBLE" && (
        <div className="alert alert-danger mb-0" role="alert">
          <strong>
            <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
            Non transmise à France Travail
          </strong>
          <div>{d.raisonNonDeclarable}</div>
          <small className="text-muted">
            France Travail ne peut pas recevoir cette sanction : elle ne sera pas traitée
            automatiquement, la sanction est à traiter à la main dans NIMS.
          </small>
        </div>
      )}

      {d.statut === "NON_DECLARABLE" && d.raisonNonDeclarable && (
        <div className="alert alert-secondary mb-0">
          <strong>
            <i className="fa fa-ban mr-1" aria-hidden="true" />
            Rien ne partira vers France Travail
          </strong>
          <div>{d.raisonNonDeclarable}</div>
          <small className="text-muted">
            Ce n'est pas une anomalie à corriger : l'EP n'a prononcé aucune sanction.
          </small>
        </div>
      )}

      <FicheSection id="sanction" title="Sanction">
        <div className="sanction-compare">
          <div className="sanction-card sanction-card--proposee">
            <div className="sanction-card__eyebrow">Décidée en EP par la CAF</div>
            <div className="sanction-card__label">{libelleDecisionEP(ep) ?? "—"}</div>
            {ep?.motifLabel && <div className="sanction-card__meta">{ep.motifLabel}</div>}
            {d.radiationDE && (
              <div className="sanction-card__badges">
                <span
                  className="sanction-tag sanction-tag--ko"
                  title="Radiation de la liste des demandeurs d'emploi"
                >
                  <i className="fa fa-briefcase" aria-hidden="true" /> Radiation DE
                </span>
              </div>
            )}
          </div>

          <div className={`sanction-card sanction-card--finale sanction-card--${ft.ton}`}>
            <div className="sanction-card__eyebrow">Chez France Travail</div>
            <div className="sanction-card__label">{ft.label}</div>
            {ft.mention && <div className="sanction-card__meta">{ft.mention}</div>}
            {d.radiation && (
              <div className="sanction-card__meta">
                Radiation : {d.radiation.label}
                {d.radiation.effectuee ? "" : " — la radiation n'a pas eu lieu"}
              </div>
            )}
          </div>
        </div>
      </FicheSection>

      <FicheSection id="decision-ep" title="Décision de l'équipe pluridisciplinaire">
        <dl className="detail-proposition__facts">
          <Fait label="Manquement">{ep?.motifLabel}</Fait>
          <Fait label="Sanction">{libelleDecisionEP(ep)}</Fait>
          <Fait label="Séance du">{ep?.dateEP ? tools.formatDate(ep.dateEP) : null}</Fait>
          <Fait label="Finalisée le">
            {ep?.dateFinalisationEP ? tools.formatDate(ep.dateFinalisationEP) : null}
          </Fait>
          <Fait label="Radiation DE">{d.radiationDE ? "Prononcée par l'EP" : null}</Fait>
        </dl>
        {!ep && (
          <p className="text-muted mb-0">
            L'inscription en EP à l'origine de cette déclaration est introuvable.
          </p>
        )}
        <div className="detail-proposition__meta">
          {[
            d.identifiantConsequenceSanction
              ? `Conséquence France Travail n° ${d.identifiantConsequenceSanction}`
              : null,
            d.identifiantManquement ? `manquement n° ${d.identifiantManquement}` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </FicheSection>

      {aUnEnvoi && (
        <FicheSection id="envois-ft" title="Envois à France Travail">
          <ul className="reponse-ft">
            {d.declaration && (
              <LigneReponse ton="ok" icon="paper-plane" code={d.declaration.codeSortie}>
                <strong>Sanction déclarée</strong>
                {d.identifiantConsequenceSanction
                  ? `, enregistrée sous la conséquence n° ${d.identifiantConsequenceSanction}`
                  : ""}
                {d.declaration.date ? `, le ${tools.formatDate(d.declaration.date)}` : ""}
              </LigneReponse>
            )}
            {d.radiation && (
              <LigneReponse
                ton={d.radiation.effectuee ? "ko" : "warn"}
                icon={d.radiation.effectuee ? "user-times" : "exclamation-circle"}
                code={d.radiation.codeSortie}
                className="reponse-ft__row--radiation"
              >
                <strong className="reponse-ft__radiation">Radiation DE</strong> : {d.radiation.label}
                {d.radiation.dateDemande
                  ? `, demandée le ${tools.formatDate(d.radiation.dateDemande)}`
                  : ""}
              </LigneReponse>
            )}
            {d.radiationDE && !d.radiation && (
              <LigneReponse ton="warn" icon="hourglass-half">
                <strong>Radiation DE</strong> prononcée par l'EP, pas encore transmise
              </LigneReponse>
            )}
          </ul>
        </FicheSection>
      )}

      <FicheSection id="chronologie" title="Chronologie">
        <Timeline
          items={chronologieDeclaration(d)}
          emptyMessage="Aucun événement."
          ariaLabel="Chronologie de la déclaration"
        />
      </FicheSection>
    </div>
  );
};

export default DetailDeclarationSanctionCAF;
